import React, { useState, useEffect } from 'react';
import { Button, Form, Input, Typography, message } from 'antd';
import {
    UserOutlined,
    LockOutlined,
    MailOutlined,
    SafetyCertificateOutlined,
    TeamOutlined,
    BarChartOutlined,
    CalendarOutlined,
    ArrowLeftOutlined,
    StopOutlined,
} from '@ant-design/icons';
import { loggingIn, checkUserFor2FA, sendLoginOTP, verifyLoginOTP, resendOTP, reset2FAState } from 'store/slices/AuthSlice';
import { useDispatch, useSelector } from 'react-redux';
import { asyncStatuses } from 'store/enum';
import PasswordReset from 'components/Auth/PasswordReset';

const { Title, Text } = Typography;


const Login = () => {
    const [loginStep, setLoginStep] = useState('email');
    const [resendTimer, setResendTimer] = useState(0);
    const [initialOtpTimer, setInitialOtpTimer] = useState(0);
    const [otpSubmitted, setOtpSubmitted] = useState(false);
    const [showPasswordReset, setShowPasswordReset] = useState(false);

    const {
        status,
        error,
        requires2FA,
        isAccountLocked,
        userEmail,
        userName,
        twoFactorStatus,
        isAuthenticated
    } = useSelector((state) => state.auth);

    const dispatch = useDispatch();
    const [form] = Form.useForm();

    useEffect(() => {
        if (!isAuthenticated) {
            dispatch(reset2FAState());
        }
    }, []);

    const handleEmailSubmit = async (values) => {
        dispatch(checkUserFor2FA({ email: values.email }));
    };

    const handlePasswordSubmit = async (values) => {
        dispatch(loggingIn(values));
    };

    const handleOTPSubmit = async (values) => {
        setOtpSubmitted(true);
        dispatch(verifyLoginOTP({ email: userEmail, otp_code: values.otp_code }));
    };

    const handleResendOTP = () => {
        if (resendTimer > 0 || initialOtpTimer > 0) return;
        dispatch(resendOTP(userEmail));
        setResendTimer(60);
        setOtpSubmitted(false);
    };

    const onFinish = async (values) => {
        switch (loginStep) {
            case 'email': handleEmailSubmit(values); break;
            case 'password': handlePasswordSubmit(values); break;
            case 'otp': handleOTPSubmit(values); break;
            default: break;
        }
    };

    const handleBackToEmail = () => {
        setLoginStep('email');
        setInitialOtpTimer(0);
        setResendTimer(0);
        setOtpSubmitted(false);
        dispatch(reset2FAState());
        form.resetFields();
    };

    const handleForgotPassword = async () => {
        const currentEmail = userEmail || form.getFieldValue('email');
        if (!currentEmail) {
            message.error('Please enter your email first');
            return;
        }
        try {
            const { requestPasswordReset } = await import('store/actions/AuthActions');
            const response = await requestPasswordReset(currentEmail);
            if (response && !response.message?.includes('Error')) {
                message.success('Password reset OTP sent to your email!');
                setShowPasswordReset(true);
            } else {
                message.error(response.message || 'Failed to send reset email');
            }
        } catch (err) {
            message.error('Failed to send reset email');
        }
    };

    const handlePasswordResetSuccess = () => {
        setShowPasswordReset(false);
        message.success('Password reset successfully! You can now log in with your new password.');
        setLoginStep('email');
        form.resetFields();
        dispatch(reset2FAState());
    };

    useEffect(() => {
        if (twoFactorStatus === asyncStatuses.SUCCESS) {
            if (isAccountLocked) {
                setLoginStep('locked');
            } else if (requires2FA) {
                dispatch(sendLoginOTP(userEmail));
                setLoginStep('otp');
                setInitialOtpTimer(30);
                setOtpSubmitted(false);
            } else {
                setLoginStep('password');
            }
        }
    }, [twoFactorStatus, requires2FA, isAccountLocked, userEmail, dispatch]);

    useEffect(() => {
        let timer;
        if (initialOtpTimer > 0) {
            timer = setTimeout(() => setInitialOtpTimer(initialOtpTimer - 1), 1000);
        } else if (resendTimer > 0) {
            timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
        }
        return () => clearTimeout(timer);
    }, [initialOtpTimer, resendTimer]);

    useEffect(() => {
        if (isAuthenticated) {
            setLoginStep('email');
            setResendTimer(0);
            setInitialOtpTimer(0);
            setOtpSubmitted(false);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (loginStep === 'email') {
            setResendTimer(0);
            setInitialOtpTimer(0);
            setOtpSubmitted(false);
        }
    }, [loginStep]);

    useEffect(() => {
        if (status === asyncStatuses.FAILED && loginStep === 'otp' && error) {
            setOtpSubmitted(false);
        }
    }, [status, error, loginStep]);

    // Switch to locked step when account gets locked during login attempts
    useEffect(() => {
        if (isAccountLocked && loginStep !== 'locked' && loginStep !== 'email') {
            setLoginStep('locked');
        }
    }, [isAccountLocked, loginStep]);

    const getFormTitle = () => {
        switch (loginStep) {
            case 'email': return 'Welcome Back';
            case 'password': return userName ? `Welcome, ${userName}!` : 'Welcome Back!';
            case 'otp': return userName ? `Hi ${userName}` : 'Verify Identity';
            default: return 'Welcome Back';
        }
    };

    const getFormSubtitle = () => {
        switch (loginStep) {
            case 'email': return 'Sign in to access your dashboard';
            case 'password': return 'Enter your password to continue';
            case 'otp': return 'Enter the verification code sent to your email';
            default: return '';
        }
    };

    const getButtonText = () => {
        switch (loginStep) {
            case 'email': return 'Continue';
            case 'password': return 'Sign In';
            case 'otp': return 'Verify & Sign In';
            default: return 'Continue';
        }
    };

    const features = [
        { icon: <TeamOutlined />, title: 'Team Management', desc: 'Manage employees, attendance & leaves' },
        { icon: <BarChartOutlined />, title: 'Sales & Invoicing', desc: 'Track deals, invoices & payments' },
        { icon: <CalendarOutlined />, title: 'Events & Calendar', desc: 'Plan meetings & sync calendars' },
    ];

    const currentEmail = userEmail || form.getFieldValue('email');

    return (
        <div className="login-page">
            {/* Left Branding Panel */}
            <div className="login-branding-panel">
                <div className="branding-shapes" />
                <div className="branding-content">
                    <div className="branding-logo-section">
                        <div className="branding-logo-icon">
                            <SafetyCertificateOutlined />
                        </div>
                        <h1 className="branding-company-name">Teson LLP</h1>
                        <p className="branding-tagline">Customer Relationship Management</p>
                    </div>

                    <div className="branding-features">
                        {features.map((f, i) => (
                            <div className="branding-feature-item" key={i}>
                                <div className="feature-icon">{f.icon}</div>
                                <div className="feature-text">
                                    <div className="feature-title">{f.title}</div>
                                    <div className="feature-desc">{f.desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="branding-footer">
                    <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
                        &copy; {new Date().getFullYear()} Teson LLP. All rights reserved.
                    </Text>
                </div>
            </div>

            {/* Right Form Panel */}
            <div className="login-form-panel">
                <div className="login-form-container">
                    {showPasswordReset ? (
                        <PasswordReset
                            embedded
                            email={currentEmail}
                            startFromOTP={true}
                            onBack={() => setShowPasswordReset(false)}
                            onSuccess={handlePasswordResetSuccess}
                        />
                    ) : (
                        <>
                            {/* Step indicator for multi-step */}
                            {loginStep !== 'email' && (
                                <Button
                                    type="text"
                                    icon={<ArrowLeftOutlined />}
                                    onClick={handleBackToEmail}
                                    className="login-back-btn"
                                >
                                    Back
                                </Button>
                            )}

                            {loginStep === 'locked' ? (
                                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                    <StopOutlined style={{ fontSize: 48, color: '#ff4d4f', marginBottom: 16 }} />
                                    <Title level={3} style={{ color: '#1a1a2e', marginBottom: 8 }}>
                                        Account Locked
                                    </Title>
                                    <Text style={{ color: '#64748b', fontSize: 15, display: 'block', marginBottom: 24 }}>
                                        Your account has been locked due to too many failed login attempts. Please contact your administrator to unlock your account.
                                    </Text>
                                    <Button type="primary" onClick={handleBackToEmail}>
                                        Back to Login
                                    </Button>
                                </div>
                            ) : (
                            <>
                            <div className="login-form-header">
                                <Title level={2} style={{ margin: 0, color: '#1a1a2e', fontWeight: 700 }}>
                                    {getFormTitle()}
                                </Title>
                                <Text style={{ color: '#64748b', fontSize: 15, marginTop: 8, display: 'block' }}>
                                    {getFormSubtitle()}
                                </Text>
                            </div>

                            {loginStep === 'otp' && (
                                <div className="login-otp-info">
                                    <MailOutlined style={{ marginRight: 8 }} />
                                    Code sent to <strong>{userEmail}</strong>
                                    {initialOtpTimer > 0 && (
                                        <div style={{ fontSize: 12, marginTop: 4, opacity: 0.7 }}>
                                            You can request a new code in {initialOtpTimer}s
                                        </div>
                                    )}
                                </div>
                            )}

                            {loginStep === 'password' && (
                                <div className="login-email-badge">
                                    <MailOutlined style={{ marginRight: 8, color: '#0050b3' }} />
                                    <span>{userEmail}</span>
                                </div>
                            )}

                            <Form
                                form={form}
                                name="login-form"
                                initialValues={{ remember: true }}
                                onFinish={onFinish}
                                layout="vertical"
                                size="large"
                                className="login-form"
                            >
                                {loginStep === 'email' && (
                                    <>
                                        <Form.Item
                                            name="email"
                                            label="Email Address"
                                            rules={[
                                                { required: true, message: 'Please enter your email' },
                                                { type: 'email', message: 'Please enter a valid email' }
                                            ]}
                                        >
                                            <Input
                                                prefix={<UserOutlined style={{ color: '#94a3b8' }} />}
                                                placeholder="you@company.com"
                                                className="login-input"
                                                disabled={status === asyncStatuses.LOADING || twoFactorStatus === asyncStatuses.LOADING}
                                            />
                                        </Form.Item>
                                    </>
                                )}

                                {loginStep === 'password' && (
                                    <>
                                        <Form.Item name="email" style={{ display: 'none' }}>
                                            <Input />
                                        </Form.Item>
                                        <Form.Item
                                            name="password"
                                            label="Password"
                                            rules={[{ required: true, message: 'Please enter your password' }]}
                                        >
                                            <Input.Password
                                                prefix={<LockOutlined style={{ color: '#94a3b8' }} />}
                                                placeholder="Enter your password"
                                                className="login-input"
                                                disabled={status === asyncStatuses.LOADING}
                                            />
                                        </Form.Item>
                                    </>
                                )}

                                {loginStep === 'otp' && (
                                    <Form.Item
                                        name="otp_code"
                                        label="Verification Code"
                                        rules={[
                                            { required: true, message: 'Please enter the OTP code' },
                                            { len: 6, message: 'OTP must be 6 digits' },
                                            { pattern: /^[0-9]+$/, message: 'OTP must contain only numbers' }
                                        ]}
                                    >
                                        <Input
                                            prefix={<LockOutlined style={{ color: '#94a3b8' }} />}
                                            placeholder="000000"
                                            maxLength={6}
                                            disabled={status === asyncStatuses.LOADING || otpSubmitted}
                                            className="login-input login-otp-input"
                                        />
                                    </Form.Item>
                                )}

                                {error && (
                                    <div className="login-error">
                                        {error.message || error.msg || 'An error occurred'}
                                    </div>
                                )}

                                <Form.Item style={{ marginBottom: 16 }}>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        className="login-submit-btn"
                                        loading={status === asyncStatuses.LOADING || twoFactorStatus === asyncStatuses.LOADING}
                                        disabled={loginStep === 'otp' && otpSubmitted}
                                        block
                                    >
                                        {getButtonText()}
                                    </Button>
                                </Form.Item>

                                {loginStep === 'password' && (
                                    <div className="login-actions">
                                        <Button type="link" onClick={handleForgotPassword} className="login-link">
                                            Forgot Password?
                                        </Button>
                                    </div>
                                )}

                                {loginStep === 'otp' && (
                                    <div className="login-actions">
                                        {initialOtpTimer === 0 && (
                                            <Button
                                                type="link"
                                                onClick={handleResendOTP}
                                                disabled={resendTimer > 0 || status === asyncStatuses.LOADING}
                                                className="login-link"
                                            >
                                                {resendTimer > 0 ? `Resend OTP (${resendTimer}s)` : 'Resend OTP'}
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </Form>

                            <div className="login-form-footer">
                                <Text style={{ color: '#94a3b8', fontSize: 12 }}>
                                    Authorized personnel only
                                </Text>
                            </div>
                            </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Login;
